import React, { memo, useContext, useState } from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { AppContext } from '@prisjakt/ui-app-context';
import Button from '@prisjakt/ui-button';
import Breadcrumb from '@prisjakt/ui-breadcrumb';
import { Column } from '@prisjakt/ui/list/variants';
import { PrivacySettingToggles } from '@prisjakt/web-app-config/types/market';
import T from '@prisjakt/ui-translate';

import { ViewportContext } from '../../components/viewport';
import TrackingSettingCheckbox from './components/tracking-setting';
import { PageContent } from 'src/components/page';
import trackingActions from './actions/tracking';
import acceptCookiesAction from '../../components/cookie-banner/actions';
import * as S from './styles';

import { PrivacySettings } from '../../globals/actions/types';
import { AppState } from '../../reducers';
import { useEffectOnMount } from 'src/hooks/useEffectOnMount';
import {
  trackInternalNavigationClick,
  InternalNavigationScope,
} from 'src/globals/tracking/tracking';

type StateProps = {
  isAuthenticated: boolean;
  settings: PrivacySettings;
};

type DispatchProps = {
  acceptCookies: typeof acceptCookiesAction;
  trackingToggleChange: (name: keyof PrivacySettings, active: boolean) => void;
  trackingToggleSave: (
    settings: PrivacySettings,
    isAuthenticated: boolean,
    showSuccessPrompt?: boolean
  ) => void;
};

type Props = DispatchProps & StateProps;

const TrackingSettings = (props: Props) => {
  const { t, market, renderMode } = useContext(AppContext);
  const { isMobile } = useContext(ViewportContext);
  const [pulseEnvId, setPulseEnvId] = useState();

  const privacySettings =
    market &&
    market.privacySettings &&
    (market.privacySettings[renderMode] || market.privacySettings.responsive);

  const waitForSourcePoint = () => {
    if (
      typeof window._sp_ !== 'undefined' &&
      typeof window._sp_.loadPrivacyManagerModal !== 'undefined'
    ) {
      window._sp_.loadPrivacyManagerModal(
        market?.sourcePointCMP?.privacyManagerID
      );
    } else {
      setTimeout(waitForSourcePoint, 250);
    }
  };

  useEffectOnMount(() => {
    if (!IS_BROWSER) {
      return;
    }

    if (market?.sourcePointCMP?.enabled) {
      waitForSourcePoint();
    }

    // @ts-ignore
    if (typeof window.pulse !== 'function') {
      return;
    }

    // @ts-ignore
    window.pulse(sdk => sdk.getEnvironmentId().then(id => setPulseEnvId(id)));
  });

  if (market?.sourcePointCMP?.enabled) {
    return <div />;
  }

  const logoUrl = `${market.cdn}/g/rfe/logos/schibsted/schibsted_dust_black.png`;

  return (
    <div>
      <PageContent>
        <Breadcrumb
          homeText={t('common', 'breadcrumb.home')}
          path={[
            {
              id: '1',
              name: t('tracking-settings', 'page.title'),
              pathName: '',
            },
          ]}
          onClick={nextPage =>
            trackInternalNavigationClick({
              scope: InternalNavigationScope.BREADCRUMB,
              nextPage,
            })
          }
        />
        <Column gap={1} container={S.ContentColumn}>
          {market.showSchibstedCoBranding && (
            <div>
              {!isMobile && (
                <S.Logo alt="Schibsted" src={logoUrl} width="128" />
              )}
              <S.SectionTitle>
                {t('tracking-settings', 'schibsted-ads.title')}
              </S.SectionTitle>
              <T
                domain="tracking-settings"
                editAdPreferencesLink={
                  <a
                    id="schibsted-ad-settings"
                    href={`https://aboutme.schibsted.com/p/${market.code}/ads/${pulseEnvId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {t(
                      'tracking-settings',
                      'schibsted-ads.edit-preferences-link'
                    )}
                  </a>
                }
                adsReadMoreLink={
                  <a
                    href={`https://info.privacy.schibsted.com/${market.code}/s005/`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {t('tracking-settings', 'schibsted-ads.read-more-link')}
                  </a>
                }
              >
                schibsted-ads.text
              </T>
              {isMobile && <S.Logo alt="Schibsted" src={logoUrl} width="128" />}
            </div>
          )}
          <div>
            <S.SectionTitle>
              {t('tracking-settings', 'personal-data.title')}
            </S.SectionTitle>
            <T
              domain="tracking-settings"
              cookiePolicyLink={
                <Link to={privacySettings?.cookieUrl}>
                  {t('common', 'cookie-banner.cookie-policy')}
                </Link>
              }
              privacyPolicyLink={
                <Link to={privacySettings?.privacyUrl}>
                  {t('common', 'cookie-banner.privacy-policy')}
                </Link>
              }
            >
              personal-data.text
            </T>
          </div>

          <Button
            color="primary"
            variant="text"
            onClick={() => {
              props.acceptCookies(privacySettings.cookieVersion, {
                ...props.settings,
                [PrivacySettingToggles.BehaviorTrackingMarketing]: false,
                [PrivacySettingToggles.BehaviorTrackingSurveys]: false,
                [PrivacySettingToggles.BehaviourTrackingImprovement]: false,
                [PrivacySettingToggles.PersonalizedAds]: false,
                [PrivacySettingToggles.Personalization]: false,
              });
              props.trackingToggleSave(
                props.settings,
                props.isAuthenticated,
                true
              );
            }}
          >
            {t('common', 'cookie-banner.reject-button')}
          </Button>

          <TrackingSettingCheckbox
            title={t('tracking-settings', 'necessary-data.title')}
            name="necessary_data"
            text={t('tracking-settings', 'necessary-data.text')}
            checked={1}
            disabled
            handleChange={() => {}}
          />
          <TrackingSettingCheckbox
            title={t(
              'tracking-settings',
              // @ts-ignore
              `${PrivacySettingToggles.BehaviourTrackingImprovement}.title`
            )}
            name={PrivacySettingToggles.BehaviourTrackingImprovement}
            handleChange={name => {
              props.trackingToggleChange(name, !props.settings[name]);
            }}
            text={t(
              'tracking-settings',
              // @ts-ignore
              `${PrivacySettingToggles.BehaviourTrackingImprovement}.text`
            )}
            checked={
              props.settings[PrivacySettingToggles.BehaviourTrackingImprovement]
            }
          />
          <TrackingSettingCheckbox
            title={t(
              'tracking-settings',
              // @ts-ignore
              `${PrivacySettingToggles.Personalization}.title`
            )}
            name={PrivacySettingToggles.Personalization}
            handleChange={name => {
              props.trackingToggleChange(name, !props.settings[name]);
            }}
            text={t(
              'tracking-settings',
              // @ts-ignore
              `${PrivacySettingToggles.Personalization}.text`
            )}
            checked={props.settings[PrivacySettingToggles.Personalization]}
            disabled={
              !props.settings[
                PrivacySettingToggles.BehaviourTrackingImprovement
              ]
            }
            indent
          />
          <TrackingSettingCheckbox
            title={t(
              'tracking-settings',
              // @ts-ignore
              `${PrivacySettingToggles.BehaviorTrackingSurveys}.title`
            )}
            name={PrivacySettingToggles.BehaviorTrackingSurveys}
            handleChange={name => {
              props.trackingToggleChange(name, !props.settings[name]);
            }}
            text={t(
              'tracking-settings',
              // @ts-ignore
              `${PrivacySettingToggles.BehaviorTrackingSurveys}.text`
            )}
            checked={
              props.settings[PrivacySettingToggles.BehaviorTrackingSurveys]
            }
          />
          <TrackingSettingCheckbox
            title={t(
              'tracking-settings',
              // @ts-ignore
              `${PrivacySettingToggles.BehaviorTrackingMarketing}.title`
            )}
            name={PrivacySettingToggles.BehaviorTrackingMarketing}
            handleChange={name => {
              props.trackingToggleChange(name, !props.settings[name]);
            }}
            text={t(
              'tracking-settings',
              // @ts-ignore
              `${PrivacySettingToggles.BehaviorTrackingMarketing}.text`
            )}
            checked={
              props.settings[PrivacySettingToggles.BehaviorTrackingMarketing]
            }
          />
          <Button
            color="positive"
            variant="outlined"
            onClick={() => {
              props.acceptCookies(privacySettings.cookieVersion);
              props.trackingToggleSave(
                props.settings,
                props.isAuthenticated,
                true
              );
            }}
          >
            {t('common', 'button.save')}
          </Button>
        </Column>
      </PageContent>
    </div>
  );
};

const mapStateToProps = (state: AppState) => ({
  isAuthenticated: state.areas.account.isAuthenticated,
  settings: state.areas.user.tracking.settings,
});

const dispatchActions = {
  acceptCookies: acceptCookiesAction,
  trackingToggleChange: trackingActions.trackingToggleChange,
  trackingToggleSave: trackingActions.trackingToggleSave,
};

export default connect(
  mapStateToProps,
  dispatchActions
)(memo(TrackingSettings));
